import type { SubmissionHandler } from 'vee-validate';
import type { AnyObject } from '@/types/helpers.types';
import type { ComposerTranslation } from 'vue-i18n';
import type { FormValidationResponseDto } from '@/types';
import { FormMode, type IBookingForms } from '@/components/tripBookingPage/tripBooking.ts';

/** Interface commune pour les composantes de formulaires (ex. SignInForm) */
type AbstractFormComponentProps<TVal extends AnyObject = AnyObject> = {
  onSubmit: SubmissionHandler<TVal>;
  initialValues?: TVal;
  isLoading?: boolean;
};

interface FormItemConfigBase {
  name: string;
}

/**
 * Propriétés de configuration appartenant à un élément du formulaire (un item),
 * un élément étant une question dans le formulaire.
 */
interface FormItemConfig extends FormItemConfigBase {
  title: string;
};

/**
 * Objet de données non-traduites, relatives à un élément dans un formulaire.  Les données comme le titre peuvent être
 * personnalisées à l'aide de valeurs (formulaire, backend, etc.) passées dans le callback prévu à cet effet.
 *
 * Cette traduction est souvent effectuée au niveau de chaque composante d'étape ("step") mais selon les besoins,
 * d'autres formulaires peuvent traîter les données au cas par cas.
 */
export interface FormItemConfigRaw<T = unknown> extends FormItemConfigBase {
  title: string | FormStepperPropCallback<T>;
  name: string;
  /** Message d'erreur devant être affiché dans le panneau ou fonction retournant une erreur personnalisée */
  messageBoxError?: string | FormStepperPropCallback<T>;
};

export type FormItemConfigCallbackOptions = {
  t: ComposerTranslation;
};

/**
 * Fonction de transformation d'une valeur de propriété à l'intérieur de l'objet de configuration des étapes.
 */
type FormStepperPropCallback<T = unknown> = (data: T | undefined, options: FormItemConfigCallbackOptions) => string;

/** Données disponibles pour la construction des titres personnalisés dans les étapes */
type FormItemConfigCallbackData = {
  formValues: Partial<IBookingForms['values']>;
  formMode: FormMode;
};

/**
 * Gestionnaire de validation/soumission des formulaires
 *
 * Si la réponse backend est positive, la fonction retournera la valeur `true`.
 * Autrement, il est possible de retourner `false` ou simplement effectuer un `throw`,
 * ce qui ne retournera alors rien.
 */
type FormSubmissionHandler<TVal extends AnyObject> = SubmissionHandler<TVal, TVal, Promise<boolean | FormValidationResponseDto | void>>;

/** Classe pour la gestion des erreurs backend de champs qui ne sont pas géré dans les formulaires */
export class UnhandledReasonError extends Error {
  /** Nom du champ en problème */
  readonly fieldName;
  /** raison retournée par le backend */
  readonly reason;
  /** Explication additionnelle fournie */
  cause: string;

  constructor(fieldName: string, reason: string) {
    super(`Erreur sur le champ de formulaire "${fieldName}"`);
    this.name = 'UnhandledReasonError';
    this.cause = `L'identifiant "${reason}" n'est pas supporté`;
    this.fieldName = fieldName;
    this.reason = reason;

    Object.setPrototypeOf(this, UnhandledReasonError.prototype);
  }
}

export type {
  AbstractFormComponentProps,
  FormItemConfig,
  FormSubmissionHandler,
  FormItemConfigBase,
  FormItemConfigCallbackData,
};
